import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  Calendar,
  ChevronDown,
  DollarSign,
  FileText,
  Globe,
  Hash,
  Layers,
  Mail,
  MapPin,
  Megaphone,
  Phone,
  Smartphone,
  User,
  X,
} from "lucide-react";
import moment from "moment";

import { getNewDevelopment } from "../../../api/dashboard";
import { money } from "../../dashboard/components/MarketSignalAlertsPanel";

/**
 * One BCI project, everything BCI sent for it (Christian Gravias, Monday
 * 12899126094, 23 Oct 2026: "Can we please make every project clickable to show
 * all the info like BCI does"). Laid out like BCI's project page: the status
 * line, Information, the project record, the description, then Project Roles
 * grouped by role group with each company and its contact.
 *
 * `row` is the list row, shown at once while the full record loads.
 */

const monthYear = (d) => (d ? moment(d).format("MMMM YYYY") : null);
const dayMonthYear = (d) => (d ? moment(d).format("D MMMM YYYY") : null);
// eslint-disable-next-line no-restricted-syntax -- a number (square metres), not a date
const area = (n) => (n ? `${Number(n).toLocaleString("en-AU")} m²` : null);
const yesNo = (b) => (b === true ? "Yes" : b === false ? "No" : null);
const tel = (p) => `tel:${String(p).replace(/[^\d+]/g, "")}`;
const site = (w) => (/^https?:\/\//i.test(w) ? w : `https://${w}`);

const Field = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 min-w-0">
    <Icon className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" aria-hidden="true" />
    <div className="text-[12px] leading-snug min-w-0">
      <span className="font-semibold text-gray-700">{label}:</span>{" "}
      <span className="text-gray-600 break-words">{value === null || value === undefined || value === "" ? "-" : value}</span>
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-[14px] font-bold text-gray-900 mt-6 mb-3">{children}</h3>
);

const ContactLine = ({ icon: Icon, href, children }) => (
  <a
    href={href}
    target={href.startsWith("http") ? "_blank" : undefined}
    rel={href.startsWith("http") ? "noreferrer" : undefined}
    className="inline-flex items-center gap-1.5 text-[12px] text-teal-700 hover:underline min-w-0"
  >
    <Icon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
    <span className="truncate">{children}</span>
  </a>
);

const RoleCard = ({ role }) => (
  <div className="border border-gray-200 rounded-lg p-3 bg-white">
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
      <div className="text-[13px] font-bold text-gray-900">{role.company || "Not disclosed"}</div>
      {role.status && (
        <span
          className={`text-[11px] font-semibold ${role.status === "Appointed" ? "text-teal-700" : "text-gray-500"}`}
        >
          {role.status}
        </span>
      )}
    </div>
    <div className="text-[12px] text-gray-500">{role.role}</div>
    {(role.phone || role.email || role.website) && (
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {role.phone && (
          <ContactLine icon={Phone} href={tel(role.phone)}>
            {role.phone}
          </ContactLine>
        )}
        {role.email && (
          <ContactLine icon={Mail} href={`mailto:${role.email}`}>
            {role.email}
          </ContactLine>
        )}
        {role.website && (
          <ContactLine icon={Globe} href={site(role.website)}>
            {role.website.replace(/^https?:\/\//i, "")}
          </ContactLine>
        )}
      </div>
    )}
    {role.contact && (
      <div className="mt-2 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-1.5 text-[12px] text-gray-700">
          <User className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
          <span className="font-semibold">{role.contact}</span>
          {role.position && <span className="text-gray-500">· {role.position}</span>}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
          {role.contact_phone && (
            <ContactLine icon={Phone} href={tel(role.contact_phone)}>
              {role.contact_phone}
            </ContactLine>
          )}
          {role.contact_mobile && (
            <ContactLine icon={Smartphone} href={tel(role.contact_mobile)}>
              {role.contact_mobile}
            </ContactLine>
          )}
          {role.contact_email && (
            <ContactLine icon={Mail} href={`mailto:${role.contact_email}`}>
              {role.contact_email}
            </ContactLine>
          )}
        </div>
      </div>
    )}
    {role.remarks && <p className="mt-2 text-[11px] text-gray-500">{role.remarks}</p>}
  </div>
);

const RoleGroup = ({ name, roles }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 text-left hover:bg-gray-100"
      >
        <span className="text-[13px] font-bold text-gray-800">
          {name}
          <span className="ml-2 text-[11px] font-semibold text-gray-400">{roles.length}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="p-3 space-y-2">
          {roles.map((r, i) => (
            <RoleCard key={`${r.role}-${r.company}-${i}`} role={r} />
          ))}
        </div>
      )}
    </div>
  );
};

const DevelopmentDetailPanel = ({ row, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [shown, setShown] = useState(false);
  const closeRef = useRef(null);

  useEffect(() => {
    let live = true;
    setDetail(null);
    setError(null);
    getNewDevelopment(row.id)
      .then((res) => live && setDetail(res.data))
      .catch(() => live && setError("Could not load this project's details."));
    return () => {
      live = false;
    };
  }, [row.id]);

  const closeFn = useRef(onClose);
  closeFn.current = onClose;
  // Once per opening: focus the close button, slide in, Esc closes, focus goes
  // back to the row that opened it.
  useEffect(() => {
    const before = document.activeElement;
    closeRef.current?.focus();
    const t = setTimeout(() => setShown(true), 10);
    const onKey = (e) => e.key === "Escape" && closeFn.current();
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      if (before && before.focus) before.focus();
    };
  }, []);

  const p = { ...row, ...(detail || {}) };
  const roleGroups = useMemo(() => {
    const groups = [];
    const by = {};
    (p.roles || []).forEach((r) => {
      const g = r.group || "Other";
      if (!by[g]) {
        by[g] = [];
        groups.push(g);
      }
      by[g].push(r);
    });
    return groups.map((g) => ({ name: g, roles: by[g] }));
  }, [p.roles]);

  const where = [p.address, p.suburb, p.state, p.postcode].filter(Boolean).join(", ");
  const mapHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    [p.address || p.name, p.suburb, "VIC", p.postcode].filter(Boolean).join(" "),
  )}`;
  const remarks = (p.remarks || "").split("\n").filter(Boolean);
  const category = [p.bci_category, p.subcategory].filter(Boolean).join(" · ");

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-labelledby="development-detail-title">
      <div
        className={`absolute inset-0 bg-gray-900/30 transition-opacity duration-300 ${shown ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full sm:max-w-[680px] bg-white shadow-[0_8px_40px_rgba(15,23,42,0.18)] flex flex-col transition-transform duration-300 ease-out ${
          shown ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="px-5 sm:px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 id="development-detail-title" className="text-[17px] font-bold text-gray-900 leading-snug">
                {p.name}
              </h2>
              <p className="text-[12px] text-gray-500 mt-0.5">{where}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a
                href={mapHref}
                target="_blank"
                rel="noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:text-teal-700 hover:border-[#55C5D0]"
                aria-label="Open in Google Maps"
                title="Open in Google Maps"
              >
                <MapPin className="w-4 h-4" />
              </a>
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55C5D0]"
                aria-label="Close project"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {p.abandoned && (
            <p className="mt-3 inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600">
              BCI marks this project as abandoned
            </p>
          )}
        </header>

        <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-8">
          {error && <p className="mt-4 text-[12px] text-red-500">{error}</p>}

          <SectionTitle>Information</SectionTitle>
          {p.status_text && (
            <div className="flex items-start gap-2 rounded-lg bg-[#55C5D0]/10 px-3 py-2.5 mb-4">
              <Megaphone className="w-4 h-4 mt-0.5 text-teal-700 flex-shrink-0" aria-hidden="true" />
              <p className="text-[12px] text-gray-700 leading-relaxed">
                <span className="font-semibold">Status:</span> {p.status_text}
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            <Field icon={Layers} label="Project Stage" value={p.stage} />
            <Field icon={DollarSign} label="Project Value" value={money(p.value)} />
            <Field icon={FileText} label="Project Status" value={p.status} />
            <Field icon={Calendar} label="Construction Start" value={monthYear(p.construction_start)} />
            <Field icon={Building2} label="Ownership Type" value={p.ownership_type} />
            <Field icon={Calendar} label="Construction End" value={monthYear(p.construction_end)} />
            <Field icon={Building2} label="Development Type" value={p.development_type} />
            <Field icon={Layers} label="Category" value={category || p.category} />
            <Field icon={Building2} label="Project Type" value={p.project_type || p.category} />
            <Field icon={Hash} label="Units" value={p.residential_units} />
            <Field icon={Layers} label="Storeys" value={p.storeys} />
            <Field icon={Layers} label="Floor Area" value={area(p.floor_area)} />
            <Field icon={MapPin} label="Council" value={p.council} />
            <Field icon={Layers} label="Site Area" value={area(p.site_area)} />
            <Field icon={FileText} label="DA Number" value={p.da_number} />
            <Field icon={User} label="Builder Appointed" value={yesNo(p.main_contractor_appointed)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 pt-4 border-t border-gray-100">
            <Field icon={Hash} label="Project ID" value={p.project_id || p.id} />
            <Field icon={Calendar} label="First Published" value={dayMonthYear(p.first_published)} />
            <Field icon={Hash} label="Project Version" value={p.version} />
            <Field icon={Calendar} label="Last Update" value={dayMonthYear(p.updated_at)} />
            <Field icon={FileText} label="Documents" value={p.docs_available ?? null} />
          </div>

          {remarks.length > 0 && (
            <>
              <SectionTitle>Description</SectionTitle>
              <ul className="list-disc pl-5 space-y-0.5 text-[12px] text-gray-600 max-w-[65ch]">
                {remarks.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </>
          )}

          <SectionTitle>Project Roles</SectionTitle>
          {!detail && !error ? (
            <div className="space-y-2" aria-label="Loading project roles">
              {[0, 1].map((i) => (
                <div key={i} className="h-16 rounded-lg bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : roleGroups.length > 0 ? (
            <div className="space-y-3">
              {roleGroups.map((g) => (
                <RoleGroup key={g.name} name={g.name} roles={g.roles} />
              ))}
            </div>
          ) : p.developer_name ? (
            <RoleCard
              role={{
                group: "Developer",
                role: p.developer_role || "Developer",
                company: p.developer_name,
                phone: p.developer_phone,
                email: p.developer_email,
                website: p.developer_website,
              }}
            />
          ) : (
            <p className="text-[12px] text-gray-500">BCI has not listed any companies on this project yet.</p>
          )}

          <p className="mt-8 text-[11px] text-gray-400">
            Source: BCI Australia. BCI project {p.id}
            {p.updated_at ? `, updated ${moment(p.updated_at).fromNow()}` : ""}.
          </p>
        </div>
      </aside>
    </div>
  );
};

export default DevelopmentDetailPanel;
